import { useState, useRef, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { photoApi } from '../api/photoApi';

// 휴대폰(모바일 브라우저) 공개 업로드 페이지 — 앱 셸/인증 없음(게스트 JWT, GatePass 발급).
// 두 경로 모두 이 페이지로 온다: ${origin}/m/photo-upload?t=<token> (기존)
// / ${origin}/u/<token> (sv-photo deepLinkUrl 표준 경로, Photo QR 핸드오프 설계 v1.0 §6).
// 진입 즉시 claim(→ 웹 QR off) 후에만 업로드 가능 (§8).
// 라이트/다크 전환과 무관한 고정 톤이라 shell-* 대신 primitive·상태 토큰으로 그린다(tokens.css 는 shell-web main.tsx 전역 로드).
type Phase = 'claiming' | 'ready' | 'uploading' | 'done' | 'claim-error' | 'error';

export function MobileHandoffUploadPage() {
  const { token: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const token = pathToken ?? searchParams.get('t');

  const [phase, setPhase] = useState<Phase>('claiming');
  const [uploadedCount, setUploadedCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // 진입 즉시 claim — 첫 claim 승리(1디바이스 정책). 실패하면 업로드 자체를 막는다.
  useEffect(() => {
    if (!token) { setErrorMsg('잘못된 접근입니다. QR을 다시 스캔해 주세요.'); setPhase('claim-error'); return; }
    let alive = true;
    void (async () => {
      try {
        await photoApi.claimHandoff(token);
        if (alive) setPhase('ready');
      } catch (e) {
        if (!alive) return;
        const status = (e as { response?: { status?: number } })?.response?.status;
        setErrorMsg(status === 409
          ? '이미 다른 기기에서 연결된 업로드 링크입니다. 새 QR을 스캔해 주세요.'
          : 'QR이 만료되었습니다. PC에서 QR을 다시 발급해 주세요.');
        setPhase('claim-error');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const handleFiles = async (fileList: FileList | null) => {
    if (!token) return;
    const files = Array.from(fileList ?? []);
    if (!files.length) return;

    setPhase('uploading');
    setErrorMsg('');
    try {
      const { data } = await photoApi.handoffUpload(token, files);
      setUploadedCount(prev => prev + (data.data.totalCount ?? data.data.photos?.length ?? files.length));
      setPhase('done');
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setErrorMsg(status === 401
        ? '업로드 시간이 만료되었습니다. PC에서 QR을 다시 발급해 주세요.'
        : '업로드에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      setPhase('error');
    }
  };

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    padding: '32px 24px',
    background: 'var(--gray-100)',
    fontFamily: 'inherit',
  };
  const buttonStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    width: '100%', maxWidth: 360, minHeight: 60,
    padding: '18px 24px', borderRadius: 16, border: 'none',
    background: 'var(--indipia-purple-600)', color: 'var(--gray-0)',
    fontSize: 17, fontWeight: 700, letterSpacing: '-0.3px',
    cursor: 'pointer', fontFamily: 'inherit',
  };

  const isUploading = phase === 'uploading';
  const canPickFile = phase === 'ready' || phase === 'uploading' || phase === 'done' || phase === 'error';

  return (
    <div style={pageStyle}>
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--gray-900)', letterSpacing: '-0.5px', margin: 0 }}>현장 사진 올리기</h1>
        <p style={{ fontSize: 14, color: 'var(--gray-600)', marginTop: 8, lineHeight: 1.6 }}>
          휴대폰 갤러리에서 사진을 골라<br />PC로 바로 보내세요
        </p>
      </div>

      {phase === 'claiming' && (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--indipia-purple-600)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      )}

      {phase === 'claim-error' && (
        <div style={{ width: '100%', maxWidth: 360, textAlign: 'center', background: 'var(--color-errorSubtle)', border: '1.5px solid var(--status-danger-400)', borderRadius: 14, padding: '16px 18px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--status-danger-600)' }}>{errorMsg}</div>
        </div>
      )}

      {canPickFile && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={e => { void handleFiles(e.target.files); e.target.value = ''; }}
          />

          <button
            style={{ ...buttonStyle, opacity: isUploading ? 0.6 : 1, cursor: isUploading ? 'not-allowed' : 'pointer' }}
            disabled={isUploading}
            onClick={() => inputRef.current?.click()}
          >
            {isUploading ? (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                업로드 중…
              </>
            ) : (
              <>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
                </svg>
                {phase === 'done' ? '사진 더 올리기' : '갤러리에서 사진 선택'}
              </>
            )}
          </button>
        </>
      )}

      {phase === 'done' && (
        <div style={{ width: '100%', maxWidth: 360, textAlign: 'center', background: 'var(--color-successSubtle)', border: '1.5px solid var(--sage-green-400)', borderRadius: 14, padding: '16px 18px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-successStrong)' }}>{uploadedCount}장 업로드 완료!</div>
          <div style={{ fontSize: 13, color: 'var(--sage-green-600)', marginTop: 4 }}>이 창을 닫으셔도 됩니다</div>
        </div>
      )}

      {phase === 'error' && (
        <div style={{ width: '100%', maxWidth: 360, textAlign: 'center', background: 'var(--color-errorSubtle)', border: '1.5px solid var(--status-danger-400)', borderRadius: 14, padding: '16px 18px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--status-danger-600)' }}>{errorMsg}</div>
        </div>
      )}
    </div>
  );
}
